/**
 * @lines98/client/services/countdown — utility для countdown до DAILY_ROLLOVER_BOUNDARY.
 *
 * Per dispatch_t-7 §2 C7 + core-daily-board.md §5.4/AC-10:
 *   - Compute ms remaining до next `21:00 UTC` rollover.
 *   - Format as `HH:MM:SS` для display.
 *   - Tick every second через `setInterval`.
 *   - Displayed in player's local time perception, но values come from UTC math
 *     (boundary is global, не local). AC-10: «в локальном времени игрока» means
 *     the difference (in hours) is computed against player's wall-clock, which
 *     IS the elapsed-ms-to-next-21:00-UTC.
 *
 * Inject-able clock: `now()` defaults к `Date.now()`. Tests pin а fixed instant
 * для deterministic assertions без `vi.useFakeTimers()` boilerplate.
 *
 * Cross-references:
 *   - dispatch_t-7_game-developer.md §2 C7.
 *   - core-daily-board.md §5.4 (countdown UX) + AC-10.
 *   - @lines98/shared/config/balance — DAILY_ROLLOVER_BOUNDARY_UTC_HOURS = 21.
 */

import { DAILY_ROLLOVER_BOUNDARY_UTC_HOURS } from '@lines98/shared/config/balance';

// =============================================================================
// Types
// =============================================================================

/** Inject-able clock (returns ms epoch). Defaults to `Date.now`. */
export type Clock = () => number;

const defaultClock: Clock = () => Date.now();

// =============================================================================
// Pure compute
// =============================================================================

/**
 * Compute milliseconds until the next `DAILY_ROLLOVER_BOUNDARY_UTC_HOURS:00:00 UTC`
 * tick. Always positive — if current instant is exactly at boundary, returns
 * а full 24h (we shouldn't be ON-boundary in а live UI tick; defensive return).
 *
 * Algorithm: build candidate boundary at today's UTC date + 21h. If candidate
 * is <= now, advance к tomorrow's. Subtract now.
 *
 * @param now — current epoch ms (typically from `Date.now()`).
 * @returns ms к next boundary, strictly positive.
 *
 * @example
 *   // At 20:59:59 UTC on 2026-05-23, next boundary is same date 21:00 UTC.
 *   msUntilNextRollover(Date.UTC(2026, 4, 23, 20, 59, 59));  // → 1000
 *
 *   // At 21:00:01 UTC on 2026-05-23, next boundary is 2026-05-24 21:00 UTC.
 *   msUntilNextRollover(Date.UTC(2026, 4, 23, 21, 0, 1));    // → 86_399_000
 */
export function msUntilNextRollover(now: number): number {
  const d = new Date(now);
  // Candidate: today's 21:00 UTC.
  const todayBoundary = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    DAILY_ROLLOVER_BOUNDARY_UTC_HOURS,
    0,
    0,
    0,
  );
  if (now < todayBoundary) {
    return todayBoundary - now;
  }
  // Past today's boundary — next is tomorrow's.
  const tomorrowBoundary = todayBoundary + 24 * 60 * 60 * 1000;
  return tomorrowBoundary - now;
}

/**
 * Format ms duration as `HH:MM:SS`. Two-digit-zero-padded fields. Caps at 23
 * hours upper bound (next-rollover is always < 24h), but we don't assert —
 * defensive против clock-skew. Negative inputs → `00:00:00`.
 */
export function formatHms(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// =============================================================================
// Live countdown — start / stop pair
// =============================================================================

/**
 * Handle returned by `startCountdown()` — caller MUST invoke `.stop()` on
 * screen unmount, otherwise the setInterval leaks across phase transitions
 * (per process-lifecycle hygiene).
 */
export interface CountdownHandle {
  stop(): void;
}

/**
 * Start а live countdown to the next rollover. Calls `onTick(hhmmss)` immediately
 * (first paint) and then every second.
 *
 * @param onTick — callback receiving the formatted `HH:MM:SS` string. Caller
 *   typically writes it to а DOM element.
 * @param options.clock — inject-able for tests; defaults to Date.now.
 * @param options.intervalMs — defaults to 1000 (per AC-10 «обновляясь раз в секунду»).
 *   Tests can pass а fake setInterval for synchronous control.
 * @param options.setInterval — inject-able timer for tests (defaults к window).
 * @param options.clearInterval — pair of above.
 */
export function startCountdown(
  onTick: (hhmmss: string, msRemaining: number) => void,
  options: {
    clock?: Clock;
    intervalMs?: number;
    setInterval?: (cb: () => void, ms: number) => unknown;
    clearInterval?: (handle: unknown) => void;
  } = {},
): CountdownHandle {
  const clock = options.clock ?? defaultClock;
  const intervalMs = options.intervalMs ?? 1000;
  // Default to global setInterval/clearInterval (works in browser + Node tests).
  const setIntervalFn =
    options.setInterval ??
    ((cb: () => void, ms: number) =>
      (globalThis as { setInterval: (cb: () => void, ms: number) => unknown }).setInterval(cb, ms));
  const clearIntervalFn =
    options.clearInterval ??
    ((handle: unknown) =>
      (globalThis as { clearInterval: (h: unknown) => void }).clearInterval(handle));

  const tick = (): void => {
    const ms = msUntilNextRollover(clock());
    onTick(formatHms(ms), ms);
  };

  // Fire immediately so caller gets initial paint без а 1-sec wait.
  tick();
  const handle = setIntervalFn(tick, intervalMs);

  return {
    stop(): void {
      clearIntervalFn(handle);
    },
  };
}
